import { EthersError, getParsedEthersError } from '@enzoferey/ethers-error-parser'
import { Box, Grid, Typography } from '@mui/material'
import axios, { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { mergeDeepRight } from 'ramda'
import { useContext, useEffect, useState } from 'react'
import ConfirmPaymentDialog from '../../components/dialogs/ConfirmPaymentDialog'
import MultistepForm, { IHandleSubmit } from '../../components/MultistepForm/MultistepForm'
import WalletContext from '../../context/WalletContext'
import { getPaymentFeeSchedule } from '../../services/blockchainService'
import { postDentalFormClaim } from '../../services/claimsService'
import { getConfig } from '../../services/miscService'
import { AdjudicateClaimResponse } from '../../services/models/adjudication'
import { FileAttachment } from '../../services/models/claims'
import { getSavedDentalForm, updateDentalForm } from '../../services/userService'
import { calculateHash } from '../../utils'
import AncillaryStep, { PLACE_OF_TREATMENT_FIELDS } from './formSteps/AncillaryStep'
import BillingDentistStep from './formSteps/BillingDentistStep'
import CoverageStep from './formSteps/CoverageStep'
import PatientInformationStep, {
    PATIENT_RELATIONSHIP_FIELDS,
} from './formSteps/PatientInformationStep'
import ResultsStep from './formSteps/ResultsStep'
import ServicesStep from './formSteps/ServicesStep'
import TreatingDentistStep from './formSteps/TreatingDentistStep'

const REQUIRED_FIELDS = [
    'coverage-payerOrgName',
    'coverage-payerDid',
    'coverage-payerEtin',
    'coverage-payerInterchangeId',
    'coverage-payerPhoneNumber',
    'coverage-payerPrefix',
    'coverage-payerFirstName',
    'coverage-payerLastName',
    'coverage-payerAddress',
    'coverage-payerState',
    'coverage-payerCity',
    'coverage-payerZipCode',
    // 'coverage-subscriberIsValid', // TODO
    'coverage-subscriberLastName',
    'coverage-subscriberFirstName',
    'coverage-subscriberAddress',
    'coverage-subscriberState',
    'coverage-subscriberCity',
    'coverage-subscriberZipCode',
    'coverage-subscriberDOB', // at least 18?
    'coverage-subscriberIdentifier',
    'coverage-subscriberPlanOrGroupNumber',
    'coverage-subscriberEmployerName',
    'patientInfo-lastName',
    'patientInfo-firstName',
    'patientInfo-address',
    'patientInfo-state',
    'patientInfo-city',
    'patientInfo-zipCode',
    'patientInfo-patientId',
    // record of services provided

    'treatingDentist-signature',
    'treatingDentist-signatureDate',
    'treatingDentist-providerId',
    'treatingDentist-licenseNumber',
    'treatingDentist-address',
    'treatingDentist-city',
    'treatingDentist-state',
    'treatingDentist-zipCode',
    'treatingDentist-phoneNumber',
    'treatingDentist-specialty',
]

const ONE_OF_REQUIRED = [
    ['coverage-statementOfActualServices', 'coverage-requestForPreAuth', 'coverage-epsdtTitleXIX'],
    ['coverage-subscriberGenderMale', 'coverage-subscriberGenderFemale'],
    ['coverage-otherCoverageNo', 'coverage-otherCoverageYes'],
    PATIENT_RELATIONSHIP_FIELDS,
    ['patientInfo-genderMale', 'patientInfo-genderFemale'],
    PLACE_OF_TREATMENT_FIELDS,
    ['ancillary-isOrthodontics-yes', 'ancillary-isOrthodontics-no'],
    ['ancillary-isProsthesis-no', 'ancillary-isProsthesis-yes'],
]

const COVERAGE_OTHER_FIELDS = [
    'coverage-otherLastName',
    'coverage-otherFirstName',
    'coverage-otherDOB',
    'coverage-otherMale',
    'coverage-otherFemale',
    'coverage-otherSubscriberId',
    'coverage-otherPlanNumber',
    'coverage-otherCarrierName',
    'coverage-otherCarrierAddress',
    'coverage-otherCarrierCity',
    'coverage-otherCarrierState',
    'coverage-otherCarrierZipCode',
]

const COVERARGE_ONE_OF_REQUIRED = [
    [
        'coverage-otherRelationshipSelf',
        'coverage-otherRelationshipSpouse',
        'coverage-otherRelationshipDependent',
        'coverage-otherRelationshipOther',
    ],
]

const BILLING_DENTIST_FIELDS = [
    'billingDentist-name',
    'billingDentist-address',
    'billingDentist-city',
    'billingDentist-state',
    'billingDentist-zipCode',
    'billingDentist-phoneNumber',
    'billingDentist-providerId',
    'billingDentist-licenseNumber',
    'billingDentist-ssn',
]

const PHONE_NUMBER_FIELDS = ['billingDentist-phoneNumber', 'treatingDentist-phoneNumber']

const hasValue = (a: any) =>
    a !== undefined && a !== null && a !== false && a.length !== 0 && (a.$D ? !isNaN(a.$D) : true)

const getErrorLocationName = (fieldName: string) => {
    const loc = fieldName.split('-')[0]
    switch (loc) {
        case 'coverage':
            return 'Coverage'
        case 'patientInfo':
            return 'Patient Information'
        case 'services':
            return 'Services'
        case 'ancillary':
            return 'Ancillary'
        case 'billingDentist':
            return 'Billing Dentist'
        case 'treatingDentist':
            return 'Treating Dentist'
        default:
            return '--'
    }
}

const SubmitDental = () => {
    const wallet = useContext(WalletContext)
    // TODO
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [claimAdjudicationResults, setClaimAdjudicationResults] =
        useState<AdjudicateClaimResponse>()
    const [errorLocations, setErrorLocations] = useState<Record<string, boolean>>()
    const [formErrors, setFormErrors] = useState<{ location: string; message: string }[]>([])
    const [formSubmitting, setFormSubmitting] = useState(false)
    const [savedForm, setSavedForm] = useState<{ [key: string]: any }>({})
    const [paymentDialogData, setPaymentDialogData] = useState<{
        infrastructureFee: number
        payerFee: number
        rhizaFee: number
        total: number
    }>()
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false)
    const [validatedForm, setValidatedForm] = useState<any>()
    const [userSignature, setUserSignature] = useState<string>()
    const [claimNumber, setClaimNumber] = useState<string>()
    const [resetForm, setResetForm] = useState(false)

    useEffect(() => {
        getSavedDentalForm().then(({ data: form }) => setSavedForm(form))
        getConfig()
            .then(async (config) => {
                if (config.data.blockchainPaymentsEnabled) {
                    await getPaymentFeeSchedule()
                        .then((payouts) => {
                            setPaymentDialogData({
                                infrastructureFee: payouts.adminPayout,
                                payerFee: payouts.payerPayout,
                                rhizaFee: payouts.otherPayout,
                                total: payouts.total,
                            })
                        })
                        .catch((err) => {
                            console.log(err)
                        })
                }
            })
            .catch((err) => {
                console.log(err)
            })
    }, [])

    const handleSubmitForm = async ({ values }: IHandleSubmit) => {
        setResetForm(false)
        let errors: any = {}
        const addErrors = (_errors: any) => {
            errors = { ...errors, ..._errors }
        }
        const missingFields = REQUIRED_FIELDS.filter((fieldName) => !hasValue(values[fieldName]))
        addErrors(missingFields.reduce((acc, key) => ({ ...acc, [key]: 'Required' }), {}))

        const missingOneOfFields = ONE_OF_REQUIRED.filter((fieldNames) =>
            fieldNames.reduce((acc, key) => (acc ? !(values[key] === true) : acc), true),
        )
        addErrors(
            missingOneOfFields.reduce(
                (acc, fieldNames) => ({
                    ...acc,
                    ...fieldNames.reduce(
                        (_acc, fieldName) => ({ ..._acc, [fieldName]: 'Select one' }),
                        {},
                    ),
                }),
                {},
            ),
        )

        if (values['coverage-otherCoverageYes']) {
            const missingCoverageFields = COVERAGE_OTHER_FIELDS.filter(
                (fieldName) => !hasValue(values[fieldName]),
            )
            addErrors(
                missingCoverageFields.reduce((acc, key) => ({ ...acc, [key]: 'Required' }), {}),
            )

            const coverageMissingOneOfFields = COVERARGE_ONE_OF_REQUIRED.filter((fieldNames) =>
                fieldNames.reduce((acc, key) => (acc ? !(values[key] === true) : acc), false),
            )
            addErrors(
                coverageMissingOneOfFields.reduce(
                    (acc, fieldNames) => ({
                        ...acc,
                        ...fieldNames.reduce(
                            (_acc, fieldName) => ({ ..._acc, [fieldName]: 'Select one' }),
                            {},
                        ),
                    }),
                    {},
                ),
            )
        }

        if (values['ancillary-isOrthodontics-yes']) {
            if (!hasValue(values['ancillary-dateAppliancePlaced'])) {
                addErrors({ 'ancillary-dateAppliancePlaced': 'Required for Orthodontics' })
            }
            if (!hasValue(values['ancillary-monthsOfTreatmentRemaining'])) {
                addErrors({ 'ancillary-monthsOfTreatmentRemaining': 'Required for Orthodontics' })
            }
        }

        if (values['ancillary-isProsthesis-yes']) {
            if (!hasValue(values['ancillary-datePriorPlacement'])) {
                addErrors({
                    'ancillary-datePriorPlacement': 'Required for Replacement of Prosthesis',
                })
            }
        }

        if (values['coverage-subscriberIsValid'] === false) {
            addErrors({
                'coverage-subscriberIsValid': 'Invalid subscriber',
            })
        }

        if (values['ancillary-treatmentFrom-auto']) {
            !hasValue(values['ancillary-autoAccidentState']) &&
                addErrors({ 'ancillary-autoAccidentState': 'Required for Auto Accident' })

            !hasValue(values['ancilllary-accidentDate']) &&
                addErrors({ 'ancillary-accidentDate': 'Required for Auto Accident' })
        }
        if (values['ancillary-treatmentFrom-other']) {
            !hasValue(values['ancillary-accidentDate']) &&
                addErrors({ 'ancillary-accidentDate': 'Required for Other Accident' })
        }

        const billingDentistPopulated = BILLING_DENTIST_FIELDS.reduce(
            (acc, fieldName) =>
                hasValue(values[fieldName])
                    ? { some: true, all: acc.all }
                    : { some: acc.some, all: false },
            { some: false, all: true },
        )
        if (!billingDentistPopulated.all && billingDentistPopulated.some) {
            addErrors(
                BILLING_DENTIST_FIELDS.reduce(
                    (acc, fieldName) => ({ ...acc, [fieldName]: 'No partially completed fields' }),
                    {},
                ),
            )
        }

        for (const phoneNumberField of PHONE_NUMBER_FIELDS) {
            if (
                hasValue(values[phoneNumberField]) &&
                values[phoneNumberField].trim().length !== 17
            ) {
                addErrors({ [phoneNumberField]: 'Invalid phone number' })
            }
        }

        const _errorLocations = Object.keys(errors).reduce(
            (acc, fieldName) => ({ ...acc, [fieldName.split('-')[0]]: true }),
            {},
        )

        setErrorLocations(_errorLocations)

        return errors
    }

    const handleFormSuccess = async (f: Record<string, any>) => {
        try {
            setFormErrors([])
            const formatTeethServiced = (fields: any, predicate: string) => {
                return Object.keys(fields)
                    .filter((svcKey) => svcKey.includes(predicate) && fields[svcKey] === true)
                    .map((toothSvcKey) => {
                        const splitTooth = toothSvcKey.split('-') // ouch
                        return splitTooth[splitTooth.length - 1]
                    })
            }

            const formPayload = {
                coverage: {
                    payerInformation: {
                        orgName: f['coverage-payerOrgName'],
                        lastName: f['coverage-payerLastName'],
                        firstName: f['coverage-payerFirstName'],
                        phoneNumber: f['coverage-payerPhoneNumber'],
                        interchangeId: f['coverage-payerInterchangeId'],
                        etin: f['coverage-payerEtin'],
                        address: {
                            streetAddress: f['coverage-payerAddress'],
                            state: f['coverage-payerState'],
                            city: f['coverage-payerCity'],
                            zipCode: f['coverage-payerZipCode'],
                        },
                    },
                    headerInformation: {
                        typeOfTransaction: {
                            statementOfServices: f['coverage-statementOfActualServices'] ?? false,
                            requestForPreAuth: f['coverage-requestForPreAuth'] ?? false,
                            epsdtTitleXix: f['coverage-epsdtTitleXIX'] ?? false,
                        },
                    },
                    subscriberInformation: {
                        lastName: f['coverage-subscriberLastName'],
                        firstName: f['coverage-subscriberFirstName'],
                        address: {
                            streetAddress: f['coverage-subscriberAddress'],
                            state: f['coverage-subscriberState'],
                            city: f['coverage-subscriberCity'],
                            zipCode: f['coverage-subscriberZipCode'],
                        },
                        middleInitial: f['coverage-subscriberMiddleInitial'],
                        suffix: f['coverage-subscriberSuffix'],
                        dateOfBirth: dayjs(f['coverage-subscriberDOB']).format('YYYY-MM-DD'),
                        sex: f['coverage-subscriberGenderMale'] ? 'M' : 'F',
                        ssn: f['coverage-subscriberIdentifier'],
                        planOrGroupNumber: f['coverage-subscriberPlanOrGroupNumber'],
                        subscriberId: f['coverage-subscriberId'],
                        employerName: f['coverage-subscriberEmployerName'],
                    },
                    otherCoverage: {}, // TODO
                },
                patientInformation: {
                    relationshipToSubscriber: f['patientInfo-relationshipSelf']
                        ? 'self'
                        : f['patientInfo-relationshipSpouse']
                        ? 'spouse'
                        : f['patientInfo-relationshipDependentChild']
                        ? 'dependent'
                        : 'other',
                    studentStatus: f['patientInfo-studentStatusFTS']
                        ? 'fullTime'
                        : f['patientInfo-studentStatusPTS']
                        ? 'partTime'
                        : undefined, // TODO: not required in form
                    lastName: f['patientInfo-lastName'],
                    firstName: f['patientInfo-firstName'],
                    middleInitial: f['patientInfo-middleInitial'],
                    suffix: f['patientInfo-suffix'],
                    address: {
                        streetAddress: f['patientInfo-address'],
                        state: f['patientInfo-state'],
                        city: f['patientInfo-city'],
                        zipCode: f['patientInfo-zipCode'],
                    },
                    sex: f['patientInfo-genderMale'] ? 'M' : 'F',
                    patientId: f['patientInfo-patientId'],
                },
                services: {
                    servicesProvided: Array.isArray(f['services-servicesProvided'])
                        ? f['services-servicesProvided'].map((svc: any) => ({
                              procedureDate: dayjs(svc.procedureDate).format('YYYY-MM-DD'),
                              areaOfOralCavity: svc.oralCavity,
                              toothSystem: svc.toothSystem,
                              toothSurface: svc.toothSurface,
                              procedureCode: svc.procedureCode,
                              description: svc.description,
                              fee: svc.fee,
                              teethOperated: formatTeethServiced(svc, 'services-teethServiced'),
                          }))
                        : [],
                    missingTeeth: formatTeethServiced(f, 'services-missingTeeth'),
                },
                ancillary: {
                    placeOfTreatment: f['ancillary-placeOfTreatment-providersOffice']
                        ? 'office'
                        : f['ancillary-placeOfTreatment-hospital']
                        ? 'hospital'
                        : f['ancillary-placeOfTreatment-ECF']
                        ? 'ecf'
                        : 'other',
                    orthodontics: {
                        isTreatmentForOrthodontics: f['ancillary-isOrthodontics-yes'] ?? false,
                        dateAppliancePlaced: dayjs(f['ancillary-dateAppliancePlaced']).format(
                            'YYYY-MM-DD',
                        ),
                        monthsOfTreatmentRemaining: f['ancillary-monthsOfTreatmentRemaining'],
                    },
                    prosthesis: {
                        isReplacementOfProsthesis: f['ancillary-isProsthesis-yes'] ?? false,
                        datePriorReplacement: f['ancillary-datePriorPlacement'],
                    },
                    accident: {
                        accidentType: f['ancillary-treatmentFrom-occupational']
                            ? 'occupational'
                            : f['ancillary-treatmentFrom-auto']
                            ? 'auto'
                            : f['ancillary-treatmentFrom-other']
                            ? 'other'
                            : undefined,
                    },
                    attachments: f['ancillary-attachments'],
                },
                billingDentist: {
                    name: f['billingDentist-name'],
                    address: {
                        streetAddress: f['billingDentist-address'],
                        city: f['billingDentist-city'],
                        state: f['billingDentist-state'],
                        zipCode: f['billingDentist-zipCode'],
                    },
                    providerId: f['billingDentist-providerId'],
                    licenseNumber: f['billingDentist-licenseNumber'],
                    ssn: f['billingDentist-ssn'],
                    phoneNumber: f['billingDentist-phoneNumber'],
                },
                treatingDentist: {
                    signed: f['treatingDentist-signature'],
                    signedDate: f['treatingDentist-signatureDate'],
                    providerId: f['treatingDentist-providerId'],
                    licenseNumber: f['treatingDentist-licenseNumber'],
                    address: {
                        streetAddress: f['treatingDentist-address'],
                        city: f['treatingDentist-city'],
                        state: f['treatingDentist-state'],
                        zipCode: f['treatingDentist-zipCode'],
                    },
                    phoneNumber: f['treatingDentist-phoneNumber'],
                    treatingProviderSpecialty: f['treatingDentist-specialty'],
                },
            }

            setValidatedForm(formPayload)

            const formPayloadWithAttachmentHashes = mergeDeepRight(formPayload, {
                ancillary: {
                    attachments: formPayload.ancillary.attachments.map(
                        (attachment: FileAttachment) => ({
                            name: attachment.name,
                            hash:
                                attachment.hash ??
                                (attachment.data && attachment.data.length
                                    ? calculateHash(attachment.data)
                                    : null),
                        }),
                    ),
                },
            })

            const signature = await wallet.signer?._signTypedData(
                { name: 'Rhiza Health' },
                { '837DentalForm': [{ name: 'data', type: 'string' }] },
                { data: JSON.stringify(formPayloadWithAttachmentHashes) },
            )

            setUserSignature(signature)

            setPaymentDialogOpen(true)
        } catch (err) {
            console.error(err)
            setFormSubmitting(false)
            const error = err as any

            let errMessage = `${err}`
            if (getParsedEthersError(err as EthersError).context === 'ACTION_REJECTED')
                errMessage = 'Wallet signature rejected'
            else if (error.message) errMessage = error.message

            if (errMessage.length > 50) errMessage = errMessage.substring(0, 50).concat('...')

            setFormErrors((prev) => [
                ...prev,
                {
                    location: 'Application error',
                    message: errMessage,
                },
            ])
        }
    }

    const handleSubmitPayment = async () => {
        setPaymentDialogOpen(false)
        try {
            if (!userSignature) {
                setFormSubmitting(false)
                setFormErrors((prev) => [
                    ...prev,
                    { location: 'Application error', message: 'No signature provided' },
                ])
                return
            }
            const {
                data: {
                    publicData: { claimNumber: _claimNumber },
                },
            } = await postDentalFormClaim({ formPayload: validatedForm, signature: userSignature })
            setClaimNumber(_claimNumber)

            // We are finally done with form submission at this point
            setFormSubmitting(false)
            setResetForm(true)
        } catch (err) {
            console.error(err)
            if (axios.isAxiosError(err)) {
                setFormSubmitting(false)
                setFormErrors((prev) => [
                    ...prev,
                    {
                        location: 'Application error',
                        message:
                            ((err as AxiosError).response?.data as any).error ??
                            (err as AxiosError).message,
                    },
                ])
            }
        }
    }

    return (
        <Box>
            <ConfirmPaymentDialog
                open={paymentDialogOpen}
                data={paymentDialogData}
                onClose={() => {
                    setPaymentDialogOpen(false)
                    setFormErrors([{ location: 'Application error', message: 'Payment declined' }])
                    setFormSubmitting(false)
                }}
                onSubmit={handleSubmitPayment}
            />
            <Grid container direction="column" style={{ padding: '40px' }}>
                <Grid item>
                    <Typography variant="h3">Dental Claim Form</Typography>
                </Grid>
                <Grid item>
                    <MultistepForm
                        formName="dental837"
                        steps={[
                            {
                                name: 'Coverage',
                                error: errorLocations?.coverage,
                                component: <CoverageStep />,
                            },
                            {
                                name: 'Patient Information',
                                error: errorLocations?.patientInfo,
                                component: <PatientInformationStep />,
                            },
                            {
                                name: 'Services',
                                error: errorLocations?.services,
                                component: <ServicesStep />,
                            },
                            // Implement later: { name: 'Authorizations', component: () => <AuthorizationsStep /> },
                            {
                                name: 'Ancillary',
                                error: errorLocations?.ancillary,
                                component: <AncillaryStep />,
                            },
                            {
                                name: 'Billing Dentist',
                                error: errorLocations?.billingDentist,
                                component: <BillingDentistStep />,
                            },
                            {
                                name: 'Treating Dentist',
                                error: errorLocations?.treatingDentist,
                                component: <TreatingDentistStep />,
                            },
                            {
                                name: 'Results',
                                component: (
                                    <ResultsStep
                                        pending={formSubmitting}
                                        formErrors={formErrors}
                                        claimNumber={claimNumber}
                                    />
                                ),
                            },
                        ]}
                        initialValues={{
                            ...savedForm,
                            'coverage-otherCoverageNo': true,
                            'ancillary-isProsthesis-no': true,
                            'ancillary-isOrthodontics-no': true,
                            'coverage-statementOfActualServices': true,
                        }}
                        lastStepIsResults
                        handleSubmit={handleSubmitForm}
                        initialRemoteValues={savedForm}
                        autoSave={{
                            enabled: true,
                            debounceTime: 2000,
                            handleSave: updateDentalForm,
                            excludeFields: [
                                'coverage-subscriberIdentifier',
                                'billingDentist-ssn',
                                'treatingDentist-signature',
                                'treatingDentist-signatureDate',
                            ],
                        }}
                        onSubmitForm={() => {
                            setFormSubmitting(true)
                        }}
                        onFailure={(errors) => {
                            setFormSubmitting(false)
                            setFormErrors(
                                Object.keys(errors).map((k) => ({
                                    location: getErrorLocationName(k),
                                    message: errors[k],
                                })),
                            )
                        }}
                        onSuccess={handleFormSuccess}
                        forceEnableTabs={formErrors.length > 0}
                        reset={resetForm}
                    />
                </Grid>
            </Grid>
        </Box>
    )
}

export default SubmitDental
